import classNames from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'

import { useIsRTL } from '@context/SessionSettingsContext'

import styles from './SwapFreeProgressBar.module.scss'

interface SwapFreeProgressBarProps {
  ratingValue: number
  red: number
  green: number
  disabled?: boolean
  isFullWidth?: boolean
  expandedRange?: boolean
}

export const SwapFreeProgressBar: FC<SwapFreeProgressBarProps> = ({
  ratingValue,
  red,
  green,
  disabled = false,
  isFullWidth = false,
  expandedRange = false,
}) => {
  const isRTL = useIsRTL()
  const circleRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [circleWidth, setCircleWidth] = useState(0)
  const [barWidth, setBarWidth] = useState(0)

  useEffect(() => {
    if (circleRef.current) setCircleWidth(circleRef.current.offsetWidth)
    if (barRef.current) setBarWidth(barRef.current.offsetWidth)
  }, [])

  const calculatePosition = (): number => {
    if (disabled) return 0
    let position: number
    if (expandedRange) {
      const range = green - red
      const effectiveMin = red - range
      const effectiveMax = green + range
      const maxPosition = barWidth > 0 ? 100 - (circleWidth / barWidth) * 100 : 100
      position = ((ratingValue - effectiveMin) / (effectiveMax - effectiveMin)) * 100
      return Math.max(0, Math.min(maxPosition, position))
    }
    position = ((ratingValue - red) / (green - red)) * 100
    return Math.max(0, Math.min(100, position))
  }

  const circlePosition = isRTL ? 100 - calculatePosition() : calculatePosition()

  return (
    <div
      ref={barRef}
      className={classNames(styles.progressBar, { [styles.fullWidth]: isFullWidth })}
    >
      <div className={classNames(styles.low, { [styles.disabled]: disabled })} />
      <div className={classNames(styles.medium, { [styles.disabled]: disabled })} />
      <div className={classNames(styles.high, { [styles.disabled]: disabled })} />
      <div
        ref={circleRef}
        className={styles.progressCircle}
        style={{ left: `${circlePosition}%` }}
      />
    </div>
  )
}
